import { useEffect, useState } from "react";
import { Plus, Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, apiError, money } from "@/lib/api";
import { Loader, PageHeader } from "@/components/common/Primitives";

const BLANK = { name: "", billing_period: "monthly", duration_days: 30, price: 0, features: [], max_products: 0, max_tables: 0, grace_days: 3, trial_days: 0, is_active: true };
const NUMS = ["duration_days", "price", "max_products", "max_tables", "grace_days"];

function PlanDialog({ plan, onClose, onSaved }) {
  const [p, setP] = useState({ ...BLANK, ...plan, featuresText: (plan.features || []).join("\n") });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const { featuresText, id, sort, created_at, ...rest } = p;
    const payload = { ...rest, features: featuresText.split("\n").map((x) => x.trim()).filter(Boolean) };
    NUMS.forEach((k) => { payload[k] = Number(payload[k]) || 0; });
    try {
      if (plan.id) await api.patch(`/admin/plans/${plan.id}`, payload); else await api.post("/admin/plans", payload);
      toast.success("Plan saved"); onSaved();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="plan-form-dialog">
        <DialogHeader><DialogTitle className="font-heading">{plan.id ? "Edit plan" : "New plan"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <Input required placeholder="Plan name" value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} data-testid="plan-name-input" />
          <div className="grid grid-cols-2 gap-3 text-sm">
            <label>Billing period
              <select className="h-9 w-full rounded-md border border-slate-200 px-2" value={p.billing_period} onChange={(e) => setP({ ...p, billing_period: e.target.value })} data-testid="plan-period-select">
                {["trial", "monthly", "quarterly", "yearly"].map((x) => <option key={x}>{x}</option>)}
              </select>
            </label>
            {[["price", "Price (₹)"], ["duration_days", "Duration (days)"], ["grace_days", "Grace days"], ["max_products", "Max items (0 = ∞)"], ["max_tables", "Max tables (0 = ∞)"]].map(([k, l]) => (
              <label key={k}>{l}<Input type="number" min="0" value={p[k]} onChange={(e) => setP({ ...p, [k]: e.target.value })} data-testid={`plan-${k}-input`} /></label>
            ))}
          </div>
          <Textarea rows={4} placeholder="Features, one per line" value={p.featuresText} onChange={(e) => setP({ ...p, featuresText: e.target.value })} data-testid="plan-features-input" />
          <label className="flex items-center gap-2 text-sm"><Switch checked={p.is_active} onCheckedChange={(v) => setP({ ...p, is_active: v })} data-testid="plan-active-switch" />Active</label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-emerald-500 hover:bg-emerald-600" data-testid="plan-save-btn">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPlans() {
  const [plans, setPlans] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/plans").then((r) => setPlans(r.data));
  useEffect(() => { load(); }, []);
  if (!plans) return <Loader />;
  return (
    <div>
      <PageHeader title="Subscription plans" subtitle="Prices in INR. Inactive plans are hidden from restaurants.">
        <Button className="bg-emerald-500 hover:bg-emerald-600" onClick={() => setEditing({})} data-testid="plan-add-btn"><Plus className="mr-1 h-4 w-4" />New plan</Button>
      </PageHeader>
      <div className="grid gap-4 md:grid-cols-3">
        {plans.map((p) => (
          <div key={p.id} className={`rounded-2xl border bg-white p-5 ${p.is_active ? "border-slate-200" : "border-dashed border-slate-200 opacity-60"}`} data-testid={`admin-plan-${p.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-heading text-lg font-semibold text-slate-900">{p.name}</p>
                <p className="text-xs capitalize text-slate-400">{p.billing_period} · {p.duration_days} days{!p.is_active && " · inactive"}</p>
              </div>
              <button onClick={() => setEditing(p)} className="p-1 text-slate-400 hover:text-slate-700" data-testid={`admin-plan-edit-${p.id}`}><Pencil className="h-4 w-4" /></button>
            </div>
            <p className="mt-3 font-heading text-2xl font-bold">{money(p.price)}</p>
            <ul className="mt-3 space-y-1 text-sm text-slate-600">{p.features.map((f) => <li key={f}>· {f}</li>)}</ul>
          </div>
        ))}
      </div>
      {editing && <PlanDialog plan={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
